export type AwardSeason = {
  /** Plural show name for demo cards, e.g. "Emmys" */
  showName: string;
  /** Marketing season label, e.g. "Emmy Season" */
  seasonLabel: string;
  /** Month when this season starts (1-12), inclusive */
  startMonth: number;
  /** Month when this season ends (1-12), inclusive */
  endMonth: number;
};

export type CurrentAwardSeason = {
  showName: string;
  seasonLabel: string;
  year: number;
};

/**
 * Major award-season windows used for marketing badges.
 * Gaps between seasons resolve to the next upcoming season.
 */
export const AWARD_SEASONS: readonly AwardSeason[] = [
  {
    showName: "Oscars",
    seasonLabel: "Oscar Season",
    startMonth: 1,
    endMonth: 3,
  },
  {
    showName: "Tonys",
    seasonLabel: "Tony Season",
    startMonth: 5,
    endMonth: 6,
  },
  {
    showName: "Emmys",
    seasonLabel: "Emmy Season",
    startMonth: 7,
    endMonth: 9,
  },
  {
    showName: "Grammys",
    seasonLabel: "Grammy Season",
    startMonth: 11,
    endMonth: 12,
  },
] as const;

function toCurrentSeason(
  season: AwardSeason,
  year: number,
): CurrentAwardSeason {
  return {
    showName: season.showName,
    seasonLabel: season.seasonLabel,
    year,
  };
}

/**
 * Picks the active award season for `now`, or the next upcoming season
 * when the current month falls in a gap (e.g. April → Tonys).
 */
export function getCurrentAwardSeason(now: Date = new Date()): CurrentAwardSeason {
  const month = now.getMonth() + 1;
  const year = now.getFullYear();

  const active = AWARD_SEASONS.find(
    (season) => month >= season.startMonth && month <= season.endMonth,
  );
  if (active) {
    return toCurrentSeason(active, year);
  }

  const upcoming = AWARD_SEASONS.find((season) => season.startMonth > month);
  if (upcoming) {
    return toCurrentSeason(upcoming, year);
  }

  // After the last season of the year (should not happen with Dec covered,
  // but keeps the helper total for any future schedule gaps).
  const firstSeason = AWARD_SEASONS[0];
  if (!firstSeason) {
    throw new Error("AWARD_SEASONS must not be empty");
  }
  return toCurrentSeason(firstSeason, year + 1);
}

export function formatSeasonLiveBadge(season: CurrentAwardSeason): string {
  return `${season.seasonLabel} ${season.year} is Live`;
}

export function formatSeasonLiveNowBadge(season: CurrentAwardSeason): string {
  return `${season.seasonLabel} ${season.year} is live now`;
}

export function formatFeaturedShowLabel(season: CurrentAwardSeason): string {
  return `${season.showName} ${season.year}`;
}
